import {
  ChannelFieldConfiguration,
  PostFields,
} from '@buffer-mono/reminders-config'

import type {
  InstagramPostMetadataInput,
  PostInputMetaData,
} from '../../graphqlTypes'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'

import { clearable } from '../shared/clearable'
import { mapInstagramPostType } from '../shared/postTypes'
import type { MapperContext } from '../../types'
import type { ServiceMapperEntry } from '../types'

// Which `channelData.instagram` keys survive for the draft's current post
// type + reminder state — the same allow-list legacy applies at save time via
// `filterChannelDataBasedOnFieldsConfig`/`getFieldsToDisplay`
// (`legacy/reminders/components/new-reminders/components/channel-fields/helpers.ts`).
//
// Direct (non-reminder) saves keep exactly that post type's `postFields`.
// Reminders keep the union of (a) `reminderFields ∩ postFields` — sent
// unconditionally — and (b) whichever `reminderFields` the user individually
// selected as a sticker (`selectedStickers`), even when a selected sticker
// isn't itself in `postFields` (this is how sticker-only fields like
// `music`/`text`/`topics`/`products`/`other` ever reach the wire at all).
//
// The two location fields are additionally gated on the target channel's
// capability (`ctx.supportsLocationTagging`): Instagram location tagging is
// Graph-API business/creator only, and the composer fans one shared draft out
// per selected profile — so a mixed selection can carry a location an
// IG-Login/personal-profile channel must never receive. This replicates
// legacy's Instagram-Login/personal strip per-profile (legacy read the first
// selected profile from the `AppStore` singleton and only stripped reminder
// saves; here every save is gated, matching the capability constraint). The
// gating predicate's source of truth is LocationComposerBar, which hides the
// location picker for the same channels.
//
// Deliberately NOT replicated: legacy falls back to *no filtering at
// all* when a post type has no config entry. Instagram's `updateType` is
// constrained to `post`/`reels`/`story` in practice, so this is unreachable;
// an unrecognized value here instead allows nothing, which is the safer
// default for a typed GraphQL contract.
function getAllowedInstagramFields(
  source: {
    updateType: string | null
    isReminder: boolean
    selectedStickers: PostFields[]
  },
  ctx: MapperContext,
): Set<PostFields> {
  const updateType = source.updateType ?? 'post'
  const config = ChannelFieldConfiguration.instagram?.postTypes[updateType]
  if (!config) return new Set()

  const { postFields, reminderFields } = config
  const allowed = new Set<PostFields>()

  if (!source.isReminder) {
    postFields.forEach((field) => allowed.add(field))
  } else {
    reminderFields
      .filter((field) => postFields.includes(field))
      .forEach((field) => allowed.add(field))
    source.selectedStickers
      .filter((field) => reminderFields.includes(field))
      .forEach((field) => allowed.add(field))
  }

  if (!ctx.supportsLocationTagging) {
    allowed.delete(PostFields.LOCATION_ID)
    allowed.delete(PostFields.LOCATION_NAME)
  }

  return allowed
}

// `ctx.channelVariant` (when present) supplies every field this mapper reads.
export function mapInstagram(
  draft: Draft,
  ctx: MapperContext,
): PostInputMetaData {
  const variant = ctx.channelVariant
  const updateType = variant ? variant.updateType : draft.updateType
  const isReminder = (variant ? variant.isReminder : draft.isReminder) ?? false
  const selectedStickers = variant
    ? variant.selectedStickers
    : draft.selectedStickers
  const data = (variant ? variant.channelData : draft.channelData)?.instagram

  const allowed = getAllowedInstagramFields(
    { updateType, isReminder, selectedStickers },
    ctx,
  )
  const canSendCollaborators =
    !isReminder && (updateType === 'post' || updateType === 'reels')
  const collaborators = clearable(
    canSendCollaborators && data?.collaborators?.length
      ? data.collaborators
      : undefined,
    ctx,
  )
  const trialReelGraduationStrategy = clearable(
    !isReminder && updateType === 'reels'
      ? data?.trial_reel_graduation_strategy
      : undefined,
    ctx,
  )

  const geolocation = {
    id: clearable(
      allowed.has(PostFields.LOCATION_ID)
        ? data?.service_geolocation_id
        : undefined,
      ctx,
    ),
    text: clearable(
      allowed.has(PostFields.LOCATION_NAME)
        ? data?.service_geolocation_name
        : undefined,
      ctx,
    ),
  }
  const hasGeolocation =
    geolocation.id !== undefined || geolocation.text !== undefined

  const stickerFields = {
    text: clearable(allowed.has(PostFields.TEXT) ? data?.text : undefined, ctx),
    music: clearable(
      allowed.has(PostFields.MUSIC) ? data?.music : undefined,
      ctx,
    ),
    products: clearable(
      allowed.has(PostFields.PRODUCTS) ? data?.products : undefined,
      ctx,
    ),
    topics: clearable(
      allowed.has(PostFields.TOPICS) ? data?.topics : undefined,
      ctx,
    ),
    other: clearable(
      allowed.has(PostFields.OTHER) ? data?.other : undefined,
      ctx,
    ),
  }
  const hasStickerFields = Object.values(stickerFields).some(
    (value) => value !== undefined,
  )

  const selectedAudio = clearable(
    !isReminder && data?.audio && updateType === 'reels'
      ? {
          audioId: data.audio.audio_id,
          type:
            data.audio.audio_type === 'original_sound'
              ? ('originalSound' as const)
              : ('music' as const),
          title: data.audio.title,
          displayArtist: data.audio.display_artist,
          creatorUsername: data.audio.creator_username,
          duration: data.audio.duration_in_ms,
          audioVolume: data.audio.audio_volume,
          videoVolume: data.audio.video_volume,
        }
      : undefined,
    ctx,
  )

  const instagram: InstagramPostMetadataInput = {
    type: mapInstagramPostType(updateType),
    shouldShareToFeed: data?.share_to_feed ?? true,
    firstComment: clearable(
      allowed.has(PostFields.FIRST_COMMENT)
        ? data?.comment_text || undefined
        : undefined,
      ctx,
    ),
    link: clearable(allowed.has(PostFields.LINK) ? data?.link : undefined, ctx),
    isAiGenerated: clearable(
      allowed.has(PostFields.IS_AI_GENERATED)
        ? data?.is_ai_generated
        : undefined,
      ctx,
    ),
    ...(selectedAudio !== undefined ? { audio: selectedAudio } : {}),
    ...(collaborators !== undefined ? { collaborators } : {}),
    ...(trialReelGraduationStrategy !== undefined
      ? { trialReelGraduationStrategy }
      : {}),
    ...(hasGeolocation ? { geolocation } : {}),
    ...(hasStickerFields ? { stickerFields } : {}),
  }

  return { instagram }
}

export const instagram: ServiceMapperEntry = {
  mapper: mapInstagram,
}
